import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Skeleton,
  Alert,
  LinearProgress
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  ShoppingCart,
  Receipt,
  People,
  Add,
  Refresh,
  Visibility,
  Description,
  AssignmentTurnedIn,
  MonetizationOn,
  AutoAwesome as SparklesIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  Cell
} from 'recharts';
import salesDashboardService, { SalesDashboardData } from '../services/salesDashboardService';
import { usePermissions } from '../hooks/usePermissions';
import PeriodSelector from '../components/common/PeriodSelector';
import { computePresetRange, DEFAULT_DATE_RANGE_PRESET } from '../utils/dateRangePresets';

const STATUS_COLORS: Record<string, string> = {
  draft: '#90A4AE',
  confirmed: '#42A5F5',
  processing: '#7E57C2',
  shipped: '#26A69A',
  delivered: '#66BB6A',
  cancelled: '#EF5350',
  pending: '#FFA726',
};

const BAR_COLORS = ['#6366F1', '#06B6D4', '#10B981', '#F59E0B', '#EF4444',
                    '#8B5CF6', '#EC4899', '#14B8A6', '#F97316', '#84CC16'];

const SalesDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dateRange, setDateRange] = useState(() => computePresetRange(DEFAULT_DATE_RANGE_PRESET));
  const [dashboardData, setDashboardData] = useState<SalesDashboardData | null>(null);
  const [error, setError] = useState<{ message: string; isForbidden: boolean } | null>(null);

  const fetchDashboardData = useCallback(async () => {
    if (dashboardData) setRefreshing(true); else setLoading(true);
    setError(null);
    try {
      const data = await salesDashboardService.getDashboardData('custom', dateRange.dateFrom, dateRange.dateTo);
      setDashboardData(data);
    } catch (err: any) {
      const status = err?.response?.status;
      setError({
        message: status === 403
          ? "You don't have permission to view sales analytics."
          : 'Failed to load dashboard data. Please try again.',
        isForbidden: status === 403
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const formatCurrency = useMemo(() => (value: number) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(value),
  []);

  const formatNumber = useMemo(() => (value: number) =>
    new Intl.NumberFormat('en-IN').format(value),
  []);

  const getStatusColor = (status: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    const map: Record<string, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
      draft: 'default', pending: 'warning', confirmed: 'info',
      processing: 'primary', shipped: 'secondary', delivered: 'success', cancelled: 'error'
    };
    return map[status.toLowerCase()] || 'default';
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="text" width={240} height={48} sx={{ mb: 2 }} />
        <Grid container spacing={3} mb={3}>
          {[0, 1, 2, 3].map((i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton variant="rounded" height={140} />
            </Grid>
          ))}
        </Grid>
        <Skeleton variant="rounded" height={300} />
      </Box>
    );
  }

  if (!dashboardData) {
    return (
      <Box p={3}>
        <Alert severity={error?.isForbidden ? 'warning' : 'error'} action={
          !error?.isForbidden && (
            <Button color="inherit" size="small" onClick={fetchDashboardData}>Retry</Button>
          )
        }>
          {error?.message || 'Failed to load dashboard data.'}
        </Alert>
      </Box>
    );
  }

  const { metrics, revenueChart, topProducts, topCustomers, orderStatusBreakdown, pipeline, recentOrders } = dashboardData;

  const quickActions = [
    { label: 'New Sales Order', icon: <Add />, variant: 'contained' as const, color: 'primary' as const, path: '/admin/documents/sales-orders/create', permission: 'sales_orders.create' },
    { label: 'New Quotation', icon: <Add />, variant: 'contained' as const, color: 'secondary' as const, path: '/admin/documents/estimates/create', permission: 'estimates.create' },
    { label: 'New Invoice', icon: <Add />, variant: 'contained' as const, color: 'success' as const, path: '/admin/documents/invoices/create', permission: 'invoices.create' },
    { label: 'View All Orders', icon: <ShoppingCart />, variant: 'outlined' as const, color: 'primary' as const, path: '/admin/documents/sales-orders', permission: 'sales_orders.read' },
  ].filter((a) => hasPermission(a.permission));

  return (
    <Box sx={{ p: 3, opacity: refreshing ? 0.6 : 1, transition: 'opacity 0.15s' }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">Sales Dashboard</Typography>
        <Box display="flex" gap={2}>
          <PeriodSelector onChange={(dateFrom, dateTo) => setDateRange({ dateFrom, dateTo })} />
          <Tooltip title="Refresh">
            <span>
              <IconButton onClick={fetchDashboardData} color="primary" disabled={refreshing}>
                <Refresh />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* ── Sarvam AI Executive Decision Hub CTA ── */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          borderRadius: 2.5,
          background: 'linear-gradient(135deg, #0F172A 0%, #1E1B4B 60%, #3B0764 100%)',
          color: '#fff',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
          boxShadow: '0 4px 20px rgba(15, 23, 42, 0.15)',
        }}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <Box sx={{ p: 1, bgcolor: 'rgba(168, 85, 247, 0.25)', borderRadius: 2, display: 'flex' }}>
            <SparklesIcon sx={{ color: '#C084FC', fontSize: 24 }} />
          </Box>
          <Box>
            <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#fff' }}>
              Sarvam AI Executive Decision Hub
            </Typography>
            <Typography variant="caption" sx={{ color: '#CBD5E1' }}>
              Instant AI answers: <strong>Which products to buy/restock</strong>, <strong>what buyers search for</strong>, and <strong>why carts are abandoned</strong>.
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          endIcon={<ArrowForwardIcon />}
          onClick={() => navigate('/admin/analytics/customers')}
          sx={{
            bgcolor: '#9333EA',
            color: '#fff',
            fontWeight: 600,
            textTransform: 'none',
            borderRadius: 2,
            px: 2,
            '&:hover': { bgcolor: '#7E22CE' },
          }}
        >
          Open Decision Hub
        </Button>
      </Paper>

      {error && (
        <Alert severity="warning" sx={{ mb: 3 }} onClose={() => setError(null)}>{error.message}</Alert>
      )}

      {/* Quick Actions */}
      {quickActions.length > 0 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Quick Actions</Typography>
          <Box display="flex" gap={2} flexWrap="wrap">
            {quickActions.map((action) => (
              <Button key={action.label} variant={action.variant} color={action.color}
                startIcon={action.icon} onClick={() => navigate(action.path)}>
                {action.label}
              </Button>
            ))}
          </Box>
        </Paper>
      )}

      {/* Key Metrics */}
      <Grid container spacing={3} mb={3}>
        {[
          {
            label: 'Total Revenue', value: formatCurrency(metrics.totalRevenue),
            growth: metrics.revenueGrowth, sub: null, icon: <Receipt sx={{ fontSize: 40, opacity: 0.3 }} color="primary" />
          },
          {
            label: 'Total Orders', value: formatNumber(metrics.totalOrders),
            growth: metrics.ordersGrowth, sub: null, icon: <ShoppingCart sx={{ fontSize: 40, opacity: 0.3 }} color="secondary" />
          },
          {
            label: 'Avg Order Value', value: formatCurrency(metrics.averageOrderValue),
            growth: null, sub: 'Per transaction', icon: <TrendingUp sx={{ fontSize: 40, opacity: 0.3 }} color="info" />
          },
          {
            label: 'Pending Orders', value: formatNumber(metrics.pendingOrders),
            growth: null, sub: 'Needs attention', icon: <People sx={{ fontSize: 40, opacity: 0.3 }} color="warning" />
          },
        ].map((m) => (
          <Grid item xs={12} sm={6} md={3} key={m.label}>
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography color="textSecondary" variant="body2" gutterBottom>{m.label}</Typography>
                    <Typography variant="h5" fontWeight="bold">{m.value}</Typography>
                    {m.growth !== null && (
                      <Box display="flex" alignItems="center" mt={1}>
                        {m.growth >= 0
                          ? <TrendingUp color="success" fontSize="small" />
                          : <TrendingDown color="error" fontSize="small" />}
                        <Typography variant="body2" color={m.growth >= 0 ? 'success.main' : 'error.main'} ml={0.5}>
                          {Math.abs(m.growth).toFixed(1)}% vs prev period
                        </Typography>
                      </Box>
                    )}
                    {m.sub && <Typography variant="body2" color="textSecondary" mt={1}>{m.sub}</Typography>}
                  </Box>
                  {m.icon}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Document Pipeline Funnel */}
      {pipeline && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Document Pipeline</Typography>
            <Grid container spacing={2} alignItems="center">
              {/* Estimates */}
              <Grid item xs={12} sm>
                <Box textAlign="center" sx={{ p: 2, bgcolor: 'primary.50', borderRadius: 2, border: '1px solid', borderColor: 'primary.200' }}>
                  <Description sx={{ fontSize: 32, color: 'primary.main', mb: 1 }} />
                  <Typography variant="h4" fontWeight="bold" color="primary.main">{pipeline.estimates}</Typography>
                  <Typography variant="body2" color="textSecondary">Estimates</Typography>
                </Box>
              </Grid>

              {/* Arrow + rate */}
              <Grid item xs="auto">
                <Box textAlign="center">
                  <Typography variant="caption" color="textSecondary" display="block">
                    {pipeline.estimateToOrderRate}%
                  </Typography>
                  <Typography sx={{ fontSize: 24, color: 'text.disabled' }}>→</Typography>
                  <Typography variant="caption" color="textSecondary" display="block">converted</Typography>
                </Box>
              </Grid>

              {/* Sales Orders */}
              <Grid item xs={12} sm>
                <Box textAlign="center" sx={{ p: 2, bgcolor: 'secondary.50', borderRadius: 2, border: '1px solid', borderColor: 'secondary.200' }}>
                  <AssignmentTurnedIn sx={{ fontSize: 32, color: 'secondary.main', mb: 1 }} />
                  <Typography variant="h4" fontWeight="bold" color="secondary.main">{pipeline.salesOrders}</Typography>
                  <Typography variant="body2" color="textSecondary">Sales Orders</Typography>
                </Box>
              </Grid>

              {/* Arrow + rate */}
              <Grid item xs="auto">
                <Box textAlign="center">
                  <Typography variant="caption" color="textSecondary" display="block">
                    {pipeline.orderToInvoiceRate}%
                  </Typography>
                  <Typography sx={{ fontSize: 24, color: 'text.disabled' }}>→</Typography>
                  <Typography variant="caption" color="textSecondary" display="block">invoiced</Typography>
                </Box>
              </Grid>

              {/* Invoices */}
              <Grid item xs={12} sm>
                <Box textAlign="center" sx={{ p: 2, bgcolor: 'success.50', borderRadius: 2, border: '1px solid', borderColor: 'success.200' }}>
                  <Receipt sx={{ fontSize: 32, color: 'success.main', mb: 1 }} />
                  <Typography variant="h4" fontWeight="bold" color="success.main">{pipeline.invoices}</Typography>
                  <Typography variant="body2" color="textSecondary">Invoices</Typography>
                </Box>
              </Grid>

              {/* Arrow */}
              <Grid item xs="auto">
                <Typography sx={{ fontSize: 24, color: 'text.disabled' }}>→</Typography>
              </Grid>

              {/* Collection */}
              <Grid item xs={12} sm>
                <Box textAlign="center" sx={{ p: 2, bgcolor: 'warning.50', borderRadius: 2, border: '1px solid', borderColor: 'warning.200' }}>
                  <MonetizationOn sx={{ fontSize: 32, color: 'warning.main', mb: 1 }} />
                  <Typography variant="h5" fontWeight="bold" color="warning.main">
                    {formatCurrency(pipeline.collectedAmount)}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">Collected</Typography>
                  {pipeline.outstandingAmount > 0 && (
                    <Typography variant="caption" color="error.main">
                      {formatCurrency(pipeline.outstandingAmount)} outstanding
                    </Typography>
                  )}
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Charts Row */}
      <Grid container spacing={3} mb={3}>
        {/* Revenue Trend */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Revenue & Orders Trend</Typography>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={revenueChart}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="left" tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
                  <YAxis yAxisId="right" orientation="right" />
                  <RechartsTooltip
                    formatter={(value: number, name: string) =>
                      name === 'Revenue (₹)' ? [formatCurrency(value), name] : [value, name]}
                  />
                  <Legend />
                  <Line yAxisId="left" type="monotone" dataKey="revenue" stroke="#6366F1" strokeWidth={2} name="Revenue (₹)" dot={false} />
                  <Line yAxisId="right" type="monotone" dataKey="orders" stroke="#10B981" strokeWidth={2} name="Orders" dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Order Status Breakdown */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Order Status</Typography>
              <Box sx={{ mt: 1 }}>
                {orderStatusBreakdown.map((item) => (
                  <Box key={item.status} mb={1.5}>
                    <Box display="flex" justifyContent="space-between" mb={0.5}>
                      <Typography variant="body2">{item.status}</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {item.count} ({item.percentage}%)
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={item.percentage}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        bgcolor: 'grey.200',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: STATUS_COLORS[item.status.toLowerCase()] || '#90A4AE',
                          borderRadius: 4,
                        }
                      }}
                    />
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Top Products Bar Chart + Top Customers */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={7}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Top Products by Revenue</Typography>
              {topProducts.length === 0 ? (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                  <Typography color="textSecondary">No sales data for this period</Typography>
                </Box>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={topProducts.slice(0, 8).map((p) => ({
                      name: p.variant !== p.name ? `${p.name} – ${p.variant}` : p.name,
                      revenue: p.revenue,
                      quantity: p.quantity,
                    }))}
                    layout="vertical"
                    margin={{ left: 16, right: 16, top: 8, bottom: 8 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 11 }} />
                    <RechartsTooltip formatter={(v: number) => [formatCurrency(v), 'Revenue']} />
                    <Bar dataKey="revenue" radius={[0, 4, 4, 0]}>
                      {topProducts.slice(0, 8).map((_, i) => (
                        <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={5}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Top Customers</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Customer</TableCell>
                      <TableCell align="right">Orders</TableCell>
                      <TableCell align="right">Revenue</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {topCustomers.map((customer) => (
                      <TableRow key={customer.id} hover>
                        <TableCell>{customer.name}</TableCell>
                        <TableCell align="right">{formatNumber(customer.totalOrders)}</TableCell>
                        <TableCell align="right">{formatCurrency(customer.totalRevenue)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Recent Orders */}
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Recent Orders</Typography>
            <Button size="small" onClick={() => navigate('/admin/documents/sales-orders')}>View All</Button>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Order ID</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recentOrders.map((order) => (
                  <TableRow key={order.id} hover>
                    <TableCell>{order.order_number}</TableCell>
                    <TableCell>{order.customer_name}</TableCell>
                    <TableCell>{new Date(order.created_at).toLocaleDateString()}</TableCell>
                    <TableCell align="right">{formatCurrency(order.total_amount)}</TableCell>
                    <TableCell>
                      <Chip label={order.status} color={getStatusColor(order.status)} size="small" />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="View Details">
                        <IconButton size="small" onClick={() => navigate(`/admin/documents/sales-orders/${order.id}`)}>
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default SalesDashboard;
